import { exportTreeToJSON } from './treeExporter';
import { incrementUsage, decrementUsage } from './firestoreApi';
import { useWhiteboardStore } from '../store/useWhiteboardStore';
import { CloudSaveService } from '../services/cloudSave/cloudSaveService';

/**
 * Interface for parallel save operations
 */
export interface ParallelSaveOptions {
  userId: string;
  canvasId?: string;
  canvasName?: string;
  description?: string;
  tags?: string[];
}

/**
 * Interface for quota operations
 */
export interface QuotaOperation {
  type: 'increment' | 'decrement';
  usageType: 'threadStarters' | 'totalThreads';
}

/**
 * Save local state to browser storage (async)
 */
export const saveLocalStateAsync = async (): Promise<void> => {
  try {
    const state = useWhiteboardStore.getState().state;
    const jsonContent = exportTreeToJSON(state);
    
    // Save to localStorage for persistence across sessions
    localStorage.setItem('threadit-whiteboard-state', jsonContent);
    localStorage.setItem('threadit-whiteboard-timestamp', Date.now().toString());
  } catch (error) {
    console.error('❌ [parallelSaveUtils] Failed to save local state:', error);
    // Don't throw - local save failure shouldn't block other operations
  }
};

/**
 * Restore local state from browser storage
 */
export const restoreLocalState = (): string | null => {
  try {
    const savedState = localStorage.getItem('threadit-whiteboard-state');
    const timestamp = localStorage.getItem('threadit-whiteboard-timestamp');
    
    if (savedState && timestamp) {
      const savedTime = parseInt(timestamp, 10);
      const currentTime = Date.now();
      const timeDiff = currentTime - savedTime;
      
      // Only restore if the saved state is less than 24 hours old
      if (timeDiff < 24 * 60 * 60 * 1000) {
        return savedState;
      } else {
        localStorage.removeItem('threadit-whiteboard-state');
        localStorage.removeItem('threadit-whiteboard-timestamp');
      }
    }
    
    return null;
  } catch (error) {
    console.error('❌ [parallelSaveUtils] Failed to restore local state:', error);
    return null;
  }
};

/**
 * Save canvas to cloud using existing save function (async)
 */
export const saveCanvasToCloudAsync = async (
  options: ParallelSaveOptions
): Promise<void> => {
  try {
    const { userId, canvasId, canvasName, description, tags } = options;
    
    if (!canvasName) {
      console.warn('⚠️ [parallelSaveUtils] No canvas name provided, skipping cloud save');
      return;
    }

    const state = useWhiteboardStore.getState().state;
    const jsonContent = exportTreeToJSON(state);
    
    const result = await CloudSaveService.saveCanvas(
      jsonContent,
      canvasName,
      userId,
      canvasId,
      description,
      tags
    );

    if (result.success) {
      // Update store with new canvas ID if it's a new canvas
      if (result.canvasId && !canvasId) {
        useWhiteboardStore.getState().setCanvasId(result.canvasId);
      }
      
      // Dispatch save event for status indicator
      window.dispatchEvent(new CustomEvent('whiteboard-saved', {
        detail: {
          type: 'cloud',
          canvasName,
          canvasId: result.canvasId || canvasId
        }
      }));
    } else {
      console.error('❌ [parallelSaveUtils] Failed to save canvas to cloud:', result.error);
    }
  } catch (error) {
    console.error('❌ [parallelSaveUtils] Error saving canvas to cloud:', error);
    // Don't throw - cloud save failure shouldn't block other operations
  }
};

/**
 * Update quota counters in cloud (async) - for increments
 */
export const updateQuotaAsync = async (
  userId: string,
  usageTypes: Array<'threadStarters' | 'totalThreads'>
): Promise<void> => {
  try {
    // Execute all quota updates in parallel
    const quotaPromises = usageTypes.map(async (usageType) => {
      try {
        await incrementUsage(userId, usageType);
      } catch (error) {
        console.error(`❌ [parallelSaveUtils] Failed to increment quota ${usageType}:`, error);
        throw error; // Re-throw to be caught by the outer catch
      }
    });

    await Promise.all(quotaPromises);
  } catch (error) {
    console.error('❌ [parallelSaveUtils] Some quota increments failed:', error);
    // Don't throw - quota update failure shouldn't block other operations
  }
};

/**
 * Decrement quota counters in cloud (async) - for deletions
 */
export const decrementQuotaAsync = async (
  userId: string,
  quotaOperations: QuotaOperation[]
): Promise<void> => {
  try {
    // Execute all quota decrements in parallel
    const quotaPromises = quotaOperations.map(async (operation) => {
      try {
        await decrementUsage(userId, operation.usageType);
      } catch (error) {
        console.error(`❌ [parallelSaveUtils] Failed to decrement quota ${operation.usageType}:`, error);
        throw error; // Re-throw to be caught by the outer catch
      }
    });

    await Promise.all(quotaPromises);
  } catch (error) {
    console.error('❌ [parallelSaveUtils] Some quota decrements failed:', error);
    // Don't throw - quota update failure shouldn't block other operations
  }
};

/**
 * Execute all save operations in parallel (async) - for note creation
 */
export const executeParallelSave = async (
  options: ParallelSaveOptions,
  usageTypes: Array<'threadStarters' | 'totalThreads'> = []
): Promise<void> => {
  try {
    // Execute all operations in parallel without awaiting
    const operations: Promise<void>[] = [];
    
    // 1. Save local state (async)
    operations.push(saveLocalStateAsync());
    
    // 2. Save to cloud (async) - only if we have a canvas name
    if (options.canvasName) {
      operations.push(saveCanvasToCloudAsync(options));
    }
    
    // 3. Update quota counters (async) - only if we have usage types
    if (usageTypes.length > 0) {
      operations.push(updateQuotaAsync(options.userId, usageTypes));
    }
    
    // Wait for all operations to complete (but don't block the UI)
    await Promise.allSettled(operations);
  } catch (error) {
    console.error('❌ [parallelSaveUtils] Error in parallel save operations:', error);
    // Don't throw - save operation failures shouldn't block other operations
  }
};

/**
 * Execute all operations in parallel (async) - for note deletion
 */
export const executeParallelDelete = async (
  options: ParallelSaveOptions,
  quotaOperations: QuotaOperation[] = []
): Promise<void> => {
  try {
    // Execute all operations in parallel without awaiting
    const operations: Promise<void>[] = [];
    
    // 1. Save local state (async) - to preserve the updated state
    operations.push(saveLocalStateAsync());
    
    // 2. Save to cloud (async) - to update the cloud with the deletion
    if (options.canvasName) {
      operations.push(saveCanvasToCloudAsync(options));
    }
    
    // 3. Decrement quota counters (async) - only if we have quota operations
    if (quotaOperations.length > 0) {
      operations.push(decrementQuotaAsync(options.userId, quotaOperations));
    }
    
    // Wait for all operations to complete (but don't block the UI)
    await Promise.allSettled(operations);
  } catch (error) {
    console.error('❌ [parallelSaveUtils] Error in parallel delete operations:', error);
    // Don't throw - delete operation failures shouldn't block other operations
  }
};
